import { FormOption, Tag } from '@/lib/models';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';

import { Select } from '@/components';
import { ReactComponent as TrashIcon } from '@/assets/icons/icon-trash.svg';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useHandleError } from '@/hooks';
import { tagsService } from '@/lib/services';

export interface BlogProgramItemValue {
	id: string;
	programId: string;
	topicTags: Tag[];
}

interface BlogProgramItemProps {
	programOptions: FormOption[];
	value: BlogProgramItemValue;
	onChange(value: BlogProgramItemValue): void;
	showDeleteButton?: boolean;
	onDeleteButtonClick?(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void;
	className?: string;
}

export const BlogProgramItem = ({
	programOptions,
	value,
	onChange,
	showDeleteButton,
	onDeleteButtonClick,
	className,
}: BlogProgramItemProps) => {
	const handleError = useHandleError();
	const [topicTagOptions, setTopicTagOptions] = useState<Tag[]>([]);

	const handleProgramSelectChange = useCallback(
		({ currentTarget }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			onChange({ ...value, programId: currentTarget.value, topicTags: [] });
		},
		[onChange, value]
	);

	const handleTopicTagTypeaheadChange = useCallback(
		(selected: Tag[]) => {
			onChange({ ...value, topicTags: selected });
		},
		[onChange, value]
	);

	useEffect(() => {
		if (!value.programId) {
			return;
		}

		const fetchTopicTags = async () => {
			try {
				const response = await tagsService.getTopicTagsByProgramId(value.programId).fetch();
				setTopicTagOptions(response.tags);
			} catch (error) {
				handleError(error);
			}
		};

		fetchTopicTags();
	}, [handleError, value.programId]);

	return (
		<div className={className}>
			<Form.Group className="mb-4">
				<Form.Label className="d-flex align-items-center justify-content-between">
					Program
					{showDeleteButton && (
						<Button variant="link text-danger" onClick={onDeleteButtonClick}>
							<TrashIcon />
						</Button>
					)}
				</Form.Label>
				<Select value={value.programId} onChange={handleProgramSelectChange}>
					<option value="" disabled>
						Select program...
					</option>
					{programOptions.map((option) => (
						<option key={option.id} value={option.id} disabled={option.disabled}>
							{option.display}
						</option>
					))}
				</Select>
			</Form.Group>
			<Form.Group>
				<Form.Label>Topics</Form.Label>
				<Typeahead
					id={value.id}
					labelKey="name"
					multiple
					selected={value.topicTags}
					onChange={(selected) => handleTopicTagTypeaheadChange(selected as Tag[])}
					options={topicTagOptions}
					placeholder="Select topics..."
					disabled={!value.programId}
				/>
			</Form.Group>
		</div>
	);
};
